import { SERVICE_KIND_OPERATION } from './surgeryService';

export const PHLEBOLOGY_OPERATION_DOCTOR_PERCENT = 65;
export const SURGEON_OPERATION_DOCTOR_PERCENT = 50;

export const PAYROLL_OPERATION_EXPENSE_FIELDS = [
  { key: 'supplies', label: 'Расходники', snake: 'operation_expense_supplies' },
  { key: 'tax', label: 'Налоги', snake: 'operation_expense_tax' },
  { key: 'nurse', label: 'Медсестра', snake: 'operation_expense_nurse' },
];

export const OPERATION_EXPENSE_DETAIL_FIELDS = [
  { key: 'analysis', label: 'Анализы', snake: 'operation_expense_analysis' },
  { key: 'doctor', label: 'Расходы врача (+ к ЗП)', snake: 'operation_expense_doctor' },
  { key: 'clinic', label: 'Расходы клиники', snake: 'operation_expense_clinic' },
  { key: 'other', label: 'Прочие', snake: 'operation_expense_other' },
  { key: 'kkm', label: 'Анестезиолог', snake: 'operation_expense_kkm' },
];

export const OPERATION_EXPENSE_FIELDS = [...PAYROLL_OPERATION_EXPENSE_FIELDS, ...OPERATION_EXPENSE_DETAIL_FIELDS];

export function defaultOperationExpenses() {
  return {
    supplies: '',
    tax: '',
    analysis: '',
    doctor: '',
    nurse: '',
    clinic: '',
    other: '',
    kkm: '',
  };
}

export function operationExpensesFromLine(row) {
  const src = row?.operationExpenses || {};
  return {
    supplies: src.supplies ?? '',
    tax: src.tax ?? '',
    analysis: src.analysis ?? '',
    doctor: src.doctor ?? '',
    nurse: src.nurse ?? '',
    clinic: src.clinic ?? '',
    other: src.other ?? '',
    kkm: src.kkm ?? '',
  };
}

function serviceDisplayName(serviceOrName) {
  return typeof serviceOrName === 'string'
    ? serviceOrName
    : serviceOrName?.name ?? serviceOrName?.serviceName ?? serviceOrName?.service_name ?? '';
}

/** Какой вид операции в каталоге: флеболог / травма / уролог / УЗИ. */
export function operationCatalogSpecialty(serviceOrName) {
  const n = String(serviceDisplayName(serviceOrName)).toLowerCase();
  if (/перевяз/.test(n)) return 'dressing';
  if (/флеболог|эвлк|склеротерап/.test(n)) return 'phleb';
  if (/травматолог|ортопед/.test(n)) return 'trauma';
  if (/уролог|обрезан/.test(n)) return 'uro';
  if (/узист/.test(n)) return 'uzi';
  if (/хирург/.test(n)) return 'surgeon';
  return '';
}

/**
 * Специальность хирурга для подбора услуги операции.
 * Садикжанов Саид — «Операция хирурга» (ЭГДС остаётся в расписании).
 * Эргешеву Саиду не трогаем.
 */
export function doctorOperationSpecialty(doctorName) {
  const n = String(doctorName || '').toLowerCase();
  if (/абдыманап|нуржигит|нуржит|флеболог/.test(n)) return 'phleb';
  if (/тиллабалди|травматолог|ортопед/.test(n)) return 'trauma';
  if (/салиев|уролог/.test(n)) return 'uro';
  if (/узист/.test(n)) return 'uzi';
  if (/садикжанов/.test(n)) return 'surgeon';
  return '';
}

export function filterOperationServicesForDoctor(services, doctorName, careType = null) {
  const list = (Array.isArray(services) ? services : []).filter((service) => {
    if (!careType) return true;
    return (service?.careType ?? service?.care_type) === careType;
  });
  const specialty = doctorOperationSpecialty(doctorName);
  if (!specialty) return [];
  return list.filter((service) => operationCatalogSpecialty(service) === specialty);
}

/** Не подставлять всем подряд первую услугу каталога («Операция флеболога»). */
export function suggestOperationServiceId(services, doctorName, careType = null) {
  const matches = filterOperationServicesForDoctor(services, doctorName, careType)
    .filter((service) => !/склеротерап/.test(String(service?.name || '').toLowerCase()));
  const match = matches[0];
  return match?.id != null ? String(match.id) : '';
}

export function doctorPerformsOperations(doctor) {
  if (!doctor) return false;
  if (doctor.performsOperations != null) return Boolean(doctor.performsOperations);
  if (doctor.performs_operations != null) return Boolean(doctor.performs_operations);
  return Boolean(doctorOperationSpecialty(doctor.name));
}

export function isPhlebologyOperationService(serviceOrName) {
  const name =
    typeof serviceOrName === 'string'
      ? serviceOrName
      : serviceOrName?.name ?? serviceOrName?.serviceName ?? serviceOrName?.service_name ?? '';
  const kind =
    typeof serviceOrName === 'object'
      ? serviceOrName?.serviceKind ?? serviceOrName?.service_kind ?? ''
      : '';
  const normalized = String(name).toLowerCase();
  if (!normalized.includes('флеболог')) return false;
  if (kind && kind !== SERVICE_KIND_OPERATION) return false;
  return true;
}

export function isPayrollOperationService(serviceOrName) {
  const name =
    typeof serviceOrName === 'string'
      ? serviceOrName
      : serviceOrName?.name ?? serviceOrName?.serviceName ?? serviceOrName?.service_name ?? '';
  const kind =
    typeof serviceOrName === 'object'
      ? serviceOrName?.serviceKind ?? serviceOrName?.service_kind ?? ''
      : '';
  if (kind === SERVICE_KIND_OPERATION) return true;
  return String(name).toLowerCase().includes('операц');
}

export function resolveOperationDoctorPercent(serviceOrName, fallback = PHLEBOLOGY_OPERATION_DOCTOR_PERCENT) {
  if (typeof serviceOrName === 'object' && serviceOrName != null) {
    const raw = serviceOrName?.doctorProfitPercent ?? serviceOrName?.doctor_profit_percent;
    if (raw != null && raw !== '') return Math.max(0, Math.min(100, Number(raw) || 0));
  }
  return fallback;
}

function readExpense(expenses, key) {
  return Math.max(0, Number(expenses?.[key]) || 0);
}

export function calcOperationSplit(amount, expenses = {}, doctorPercent = PHLEBOLOGY_OPERATION_DOCTOR_PERCENT) {
  const base = Math.max(0, Number(amount) || 0);
  const analysis = readExpense(expenses, 'analysis');
  const doctorExpense = readExpense(expenses, 'doctor');
  const nurse = readExpense(expenses, 'nurse');
  const clinicExpense = readExpense(expenses, 'clinic');
  const other = readExpense(expenses, 'other');
  const kkm = readExpense(expenses, 'kkm');
  const supplies = readExpense(expenses, 'supplies');
  const tax = readExpense(expenses, 'tax');

  const totalExpenses = Math.round((analysis + doctorExpense + nurse + clinicExpense + other + kkm + supplies + tax) * 100) / 100;
  const clean = Math.max(0, Math.round((base - totalExpenses) * 100) / 100);
  const percent = Math.max(0, Math.min(100, Number(doctorPercent) || 0));
  // % от чистой + компенсация «Расходы врача»
  const doctorPayout = Math.round((clean * (percent / 100) + doctorExpense) * 100) / 100;
  // Расходы клиники, медсестра и анестезиолог целиком уходят в долю клиники.
  const clinicIncome = Math.round((clean * ((100 - percent) / 100) + clinicExpense + nurse + kkm) * 100) / 100;

  return {
    amount: base,
    totalExpenses,
    cleanProfit: clean,
    doctorPayout,
    nursePayout: 0,
    clinicIncome,
    doctorPercent: percent,
  };
}

export function calcPhlebologyOperationSplit(amount, expenses = {}) {
  return calcOperationSplit(amount, expenses, PHLEBOLOGY_OPERATION_DOCTOR_PERCENT);
}

export function buildOperationExpensePayload(expenses = {}) {
  return {
    operation_expense_supplies: readExpense(expenses, 'supplies'),
    operation_expense_tax: readExpense(expenses, 'tax'),
    operation_expense_analysis: readExpense(expenses, 'analysis'),
    operation_expense_doctor: readExpense(expenses, 'doctor'),
    operation_expense_nurse: readExpense(expenses, 'nurse'),
    operation_expense_clinic: readExpense(expenses, 'clinic'),
    operation_expense_other: readExpense(expenses, 'other'),
    operation_expense_kkm: readExpense(expenses, 'kkm'),
  };
}

export function hasOperationDetailExpenses(expenses = {}) {
  return OPERATION_EXPENSE_DETAIL_FIELDS.some((field) => readExpense(expenses, field.key) > 0);
}
